import React, { useMemo } from "react";
import { helperService } from "../../services/helper.service";

import { Constants } from "@eagerdog/constants";
import { IDog, IDogProposedChanges, IUser } from "@eagerdog/interfaces";

import styles from "./DogPortrait.module.scss";

import { Modal, useModal } from "../../components/Modal/Modal";
import DogForm from "../../components/StepForm/DogForm/DogForm";
import { IShowDogs } from "../../routes/EventRegister/EventRegister";

interface IProps {
  dog?: IDog,
  dogCallName?: string,
  showName?: boolean,
  edit?: boolean,
  show?: IShowDogs,
  onEditDog?(dog: IDog): void,
  needsRegistrationUpdate?: boolean,
  className?: string,
  sanctioningClub?: string // Add sanctioning club to get the correct registered name
  isAdmin?: boolean // Whether user is admin/manager for the event's club
  isDisabled?: boolean
}

const DogPortrait: React.FC<IProps> = (props) => {
  const { isShown, toggle } = useModal();
  const user:IUser = helperService.getUser();

  const canEdit = () => {
    // If user is admin/manager for this event's club, always allow editing
    // This enables proposed changes when club managers register on behalf of users
    if (props.isAdmin) {
      return true;
    }
    
    // For non-club-manager users (owners), always allow editing their own dogs
    if (user.user_type !== Constants.user_type.club_manager) {
      return true;
    }
    
    // For club managers without isAdmin prop set, check if they created this show
    if (props.show?.created_by === user._id && props.show?.club_id === props.dog?.club_id) {
      return true;
    }
    
    return false;
  }

  // Get the registered name for the tooltip
  const getRegisteredName = () => {
    if (!props.dog) return '';
    
    // If we have a sanctioning club, try to get the registered name from club registrations
    if (props.sanctioningClub && props.dog.club_registrations) {
      const clubReg = props.dog.club_registrations.find(cr => cr.sanctioning_club === props.sanctioningClub);
      if (clubReg?.registered_name) {
        return clubReg.registered_name;
      }
    }
    
    // Fallback to the main registered name
    return props.dog.registered_name || '';
  }

  const registeredName = getRegisteredName();

  const filteredProposedChanges = useMemo(() => {
    // If dog has proposed changes for this event, use them
    // This happens when a club manager edits a dog on behalf of an owner
    if (!props.dog?.proposed_changes || props.dog.proposed_changes.length === 0) {
      return undefined;
    }
    
    const found = props.dog.proposed_changes.find(
      (pc) => pc.event_id === props.show?.event_id
    );
    
    return found;
  }, [props.dog?.proposed_changes, props.show?.event_id]);

  return (
    <div 
      className={`${styles.DogPortrait} ${props.className ? props.className : ''} ${props.needsRegistrationUpdate ? styles.needsUpdate : ''}`}
      title={registeredName ? `Registered Name: ${registeredName}` : undefined}
    >
        <div className={`${styles.imageWrap} ${props.className ? props.className : ''}`}>
          <>{props.dog?.image_url && <img src={props.dog?.image_url} alt="Dog Portrait" />}</>
          <>{(props.dog?.image_url === undefined || props.dog?.image_url.length === 0) && <span className={`${styles.initial} ${props.className ? props.className : ''}`}>{props.dog?.call_name.substring(0, 1).toUpperCase()}</span>}</>
          {props.needsRegistrationUpdate && <div className={styles.updateIndicator}>⚠️</div>}
        </div>
        {(props.className?.includes('selected') || props.className?.includes('notSelected')) && (
          <div className={`${styles.selectionCheckbox} ${props.className?.includes('selected') && !props.className?.includes('notSelected') ? styles.checked : ''}`} />
        )}
        {props.showName !== false && <span className={`${styles.dogName} ${props.className ? props.className : ''}`}>{props.dogCallName ?? props.dog?.call_name}</span>}
        {props.edit && canEdit() && <div className={`${styles.editWrap} ${props.isDisabled ? styles.disabledEdit : ""}`} onClick={(e) => { e.stopPropagation(); toggle(); }}>
        Edit Dog
        </div>}
        {<Modal
          className={"dogForm"}
          isShown={isShown}
          hide={toggle}
          modalContent={
            <DogForm
              dog={props.dog}
              proposedChanges={filteredProposedChanges as IDogProposedChanges | undefined}
              eventId={props.show?.event_id}
              isAdmin={props.isAdmin}
              onFinish={(dog) => {
                toggle();
                if (typeof props.onEditDog === "function" && dog) props.onEditDog(dog);
              }}
              onDogUpdated={(dog) => {
                if (typeof props.onEditDog === "function") props.onEditDog(dog);
              }}
            />
          }
        />}
    </div>
  );
};

export default DogPortrait;